import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { db } from "@/api/db";
import { Plus, Trash2, ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const emptyQuestion = { question: "", options: ["", "", "", ""], correct_index: 0 };

export default function AdminExam() {
  const urlParams = new URLSearchParams(window.location.search);
  const levelId = urlParams.get("level_id");
  const trackId = urlParams.get("track_id");

  const [level, setLevel] = useState(null);
  const [exam, setExam] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", passing_score: 70, time_limit_minutes: "", points: 50, questions: [{ ...emptyQuestion, options: ["", "", "", ""] }] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (levelId) loadData(); }, [levelId]);

  async function loadData() {
    const [lvls, exams] = await Promise.all([
      db.entities.Level.filter({ id: levelId }),
      db.entities.Exam.filter({ level_id: levelId }),
    ]);
    setLevel(lvls[0] || null);
    const e = exams[0] || null;
    setExam(e);
    if (e) {
      setForm({
        title: e.title || "", description: e.description || "",
        passing_score: e.passing_score || 70, time_limit_minutes: e.time_limit_minutes || "",
        points: e.points || 50, questions: e.questions?.length ? e.questions : [{ ...emptyQuestion, options: ["", "", "", ""] }],
      });
    }
    setLoading(false);
  }

  function updateQuestion(qi, field, value) {
    setForm(f => {
      const qs = [...f.questions];
      qs[qi] = { ...qs[qi], [field]: value };
      return { ...f, questions: qs };
    });
  }

  function updateOption(qi, oi, value) {
    setForm(f => {
      const qs = [...f.questions];
      const opts = [...qs[qi].options];
      opts[oi] = value;
      qs[qi] = { ...qs[qi], options: opts };
      return { ...f, questions: qs };
    });
  }

  function addQuestion() {
    setForm(f => ({ ...f, questions: [...f.questions, { question: "", options: ["", "", "", ""], correct_index: 0 }] }));
  }

  function removeQuestion(qi) {
    setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== qi) }));
  }

  async function handleSave() {
    setSaving(true);
    const data = {
      ...form, level_id: levelId, track_id: trackId,
      passing_score: Number(form.passing_score),
      time_limit_minutes: form.time_limit_minutes ? Number(form.time_limit_minutes) : null,
      points: Number(form.points),
    };
    if (exam) {
      await db.entities.Exam.update(exam.id, data);
    } else {
      await db.entities.Exam.create(data);
    }
    await loadData();
    setSaving(false);
    alert("Prova salva com sucesso!");
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <Link to={createPageUrl("AdminLevels") + `?track_id=${trackId}`} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar aos níveis
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Prova Final — {level?.title}</h1>
        <p className="text-gray-500 mt-1">{exam ? "Editando prova existente" : "Criar nova prova"}</p>
      </div>

      {/* Exam Settings */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">Configurações</h2>
          <div>
            <label className="text-sm font-medium text-gray-700">Título da Prova *</label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Prova Final — Nível Júnior" className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Descrição</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 resize-none" rows={2} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Nota mínima (%)</label>
              <Input type="number" value={form.passing_score} onChange={e => setForm(f => ({ ...f, passing_score: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Tempo (min)</label>
              <Input type="number" value={form.time_limit_minutes} onChange={e => setForm(f => ({ ...f, time_limit_minutes: e.target.value }))} placeholder="Livre" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Pontos ao passar</label>
              <Input type="number" value={form.points} onChange={e => setForm(f => ({ ...f, points: e.target.value }))} className="mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Questões ({form.questions.length})</h2>
          <Button onClick={addQuestion} variant="outline" className="gap-2 h-8 text-sm">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </Button>
        </div>

        {form.questions.map((q, qi) => (
          <Card key={qi} className="border-0 shadow-sm">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-sm font-bold text-gray-400 mt-2">{qi + 1}.</span>
                <div className="flex-1">
                  <Input
                    value={q.question}
                    onChange={e => updateQuestion(qi, "question", e.target.value)}
                    placeholder="Digite a pergunta..."
                    className="font-medium"
                  />
                </div>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 flex-shrink-0 mt-0" onClick={() => removeQuestion(qi)} disabled={form.questions.length === 1}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>

              <div className="space-y-2 pl-6">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct_${qi}`}
                      checked={q.correct_index === oi}
                      onChange={() => updateQuestion(qi, "correct_index", oi)}
                      className="accent-emerald-500 flex-shrink-0"
                    />
                    <Input
                      value={opt}
                      onChange={e => updateOption(qi, oi, e.target.value)}
                      placeholder={`Opção ${oi + 1}${q.correct_index === oi ? " (correta)" : ""}`}
                      className={`h-8 text-sm ${q.correct_index === oi ? "border-emerald-300 bg-emerald-50" : ""}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button onClick={handleSave} disabled={!form.title || saving} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 gap-2 text-base">
        <Save className="w-4 h-4" />
        {saving ? "Salvando..." : exam ? "Atualizar Prova" : "Criar Prova"}
      </Button>
    </div>
  );
}